import React from 'react';
import { cn } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlay, faPause, faVolumeHigh, faVolumeXmark, faBackward, faForward, faHeart } from '@fortawesome/free-solid-svg-icons';

export type VideoFullscreenModalProps = {
  src: string;
  poster?: string;
  isOpen: boolean;
  isDarkMode?: boolean;
  onClose: () => void;
  creatorName?: string;
};

const SEEK_STEP = 10; // seconds

const VideoFullscreenModal: React.FC<VideoFullscreenModalProps> = ({ src, poster, isOpen, isDarkMode, onClose, creatorName }) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [current, setCurrent] = React.useState(0);
  const [liked, setLiked] = React.useState(false);
  const [showUi, setShowUi] = React.useState(true);
  const hideTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const fmt = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (isPlaying) { v.pause(); setIsPlaying(false); }
    else { if (v.src !== src) v.src = src; v.play(); setIsPlaying(true); }
  };
  const toggleMute = () => { const v = videoRef.current; if (!v) return; v.muted = !v.muted; setIsMuted(v.muted); };
  const seek = (delta: number) => { const v = videoRef.current; if (!v) return; v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + delta)); };
  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => { const v = videoRef.current; if (!v) return; const val = parseFloat(e.target.value); v.currentTime = val; setCurrent(val); };

  const showControlsTemporarily = () => {
    setShowUi(true);
    if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = window.setTimeout(() => setShowUi(false), 2000) as any;
  };

  return (
    <div className={cn('fixed inset-0 z-[9999] flex items-center justify-center', isDarkMode ? 'bg-black/90' : 'bg-black/85')} onClick={onClose}>
      <div className="relative w-full h-full max-w-screen-2xl max-h-screen p-4" onClick={(e) => e.stopPropagation()} onMouseMove={showControlsTemporarily}>
        <div className="relative w-full h-full rounded-xl overflow-hidden bg-black">
          <video
            ref={videoRef}
            poster={poster}
            preload="metadata"
            className="absolute inset-0 h-full w-full object-contain"
            onLoadedMetadata={(e) => { setDuration(e.currentTarget.duration || 0); setIsMuted(false); e.currentTarget.muted = false; }}
            onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
            onEnded={() => setIsPlaying(false)}
            autoPlay
          />
          {/* top bar */}
          <div className={cn('absolute top-0 inset-x-0 p-3 flex items-center justify-between bg-gradient-to-b from-black/50 to-transparent transition-opacity', showUi ? 'opacity-100' : 'opacity-0')}>
            <div className={cn('text-sm font-medium', 'text-white')}>{creatorName || ''}</div>
            <button className="p-2 rounded-md text-white" onClick={onClose} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
            </button>
          </div>
          {/* controls */}
          <div className={cn('absolute bottom-0 inset-x-0 p-4 space-y-3 bg-gradient-to-t from-black/60 to-transparent text-white transition-opacity', showUi ? 'opacity-100' : 'opacity-0')}>
            <div className="flex items-center gap-3 justify-center">
              <button className="p-2 rounded-md" onClick={() => seek(-SEEK_STEP)} aria-label="Seek backward">
                <FontAwesomeIcon icon={faBackward} className="w-5 h-5" />
              </button>
              <button className="p-3 rounded-full" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
                {isPlaying ? <FontAwesomeIcon icon={faPause} className="w-6 h-6" /> : <FontAwesomeIcon icon={faPlay} className="w-6 h-6" />}
              </button>
              <button className="p-2 rounded-md" onClick={() => seek(SEEK_STEP)} aria-label="Seek forward">
                <FontAwesomeIcon icon={faForward} className="w-5 h-5" />
              </button>
              <button className="p-2 rounded-md" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
                {isMuted ? <FontAwesomeIcon icon={faVolumeXmark} className="w-5 h-5" /> : <FontAwesomeIcon icon={faVolumeHigh} className="w-5 h-5" />}
              </button>
              <button className="p-2 rounded-md" onClick={() => setLiked((v) => !v)} aria-label="Like">
                <FontAwesomeIcon icon={faHeart} className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <input type="range" min={0} max={Math.max(1, duration)} step={0.1} value={current} onChange={onSeek} className="flex-1 h-1.5 accent-purple-500" />
              <div className="text-xs tabular-nums w-[80px] text-right">{fmt(current)} / {fmt(duration)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoFullscreenModal;
